const CLOUD_NAME = 'yolkstudio';
const BASE = `https://res.cloudinary.com/${CLOUD_NAME}/image/upload`;

const FOLDER = 'wefund/avatar';

export const LOGO_WIDTH = 127;
export const LOGO_HEIGHT = 50;
export const ICON_SIZE = 22;
export const AVATAR_SIZE = 70;

const RETINA = 2;

// Frames are doubled in the file to play twice: Cloudinary rewrites any GIF loop count to infinite.
export const logoUrl = `${BASE}/${FOLDER}/reuni-logo-neutral-twice.gif`;

export type IconName = 'phone' | 'mail' | 'link';

export const iconUrl = (name: IconName) => `${BASE}/f_png,w_${ICON_SIZE * RETINA}/${FOLDER}/icon-${name}-neutral`;

export const NAME_FONT = 'Montserrat';
export const NAME_FONT_SIZE = 22;
const NAME_LETTER_SPACING = -1;

const NAME_GRADIENT = `${FOLDER}/name-gradient-neutral`;

// Commas and slashes need double escaping or they split the transformation.
const encodeOverlayText = (text: string) =>
	encodeURIComponent(text).replace(/%2C/gi, '%252C').replace(/%2F/gi, '%252F');

const nameLayer = (name: string, scale: number) =>
	`l_text:${NAME_FONT}_${NAME_FONT_SIZE * scale}_medium` +
	`_letter_spacing_${NAME_LETTER_SPACING * scale}:${encodeOverlayText(name)}` +
	`,fl_cutter,fl_layer_apply`;

const nameUrl = (name: string, scale: number, box: { width: number; height: number }) =>
	`${BASE}/c_scale,w_${box.width},h_${box.height}/${nameLayer(name, scale)}/f_png/${NAME_GRADIENT}`;

export type NameImage = {
	url: string;
	width: number;
	height: number;
};

const measured = new Map<string, NameImage>();

export const getMeasuredNameImage = (name: string): NameImage | null => measured.get(name.trim()) ?? null;

export const measureNameImage = (name: string): Promise<NameImage | null> => {
	const key = name.trim();

	if (!key) {
		return Promise.resolve(null);
	}

	const cached = measured.get(key);

	if (cached) {
		return Promise.resolve(cached);
	}

	return new Promise((resolve) => {
		const probe = new Image();

		probe.onload = () => {
			const sourceWidth = probe.naturalWidth;
			const sourceHeight = probe.naturalHeight;

			const entry: NameImage = {
				url: nameUrl(key, RETINA, { width: sourceWidth, height: sourceHeight }),
				width: Math.round(sourceWidth / RETINA),
				height: Math.round(sourceHeight / RETINA),
			};

			measured.set(key, entry);
			resolve(entry);
		};

		probe.onerror = () => resolve(null);

		// Unscaled: the cut-out size depends only on the text and the font.
		probe.src = `${BASE}/${nameLayer(key, RETINA)}/f_png/${NAME_GRADIENT}`;
	});
};

// Used when the size has not been measured yet. The box is estimated from the
// character count, which is close enough to keep the gradient from going flat.
export const fallbackNameUrl = (name: string): string | null => {
	const key = name.trim();

	if (!key) {
		return null;
	}

	return nameUrl(key, 1, {
		width: Math.round(key.length * NAME_FONT_SIZE * 0.55),
		height: Math.round(NAME_FONT_SIZE * 1.4),
	});
};
